import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { FEATURES } from '@/config/appConfig';
import type { SpeechError, SpeechFinalEvent, SpeechSession, SpeechState } from './types';
import { assemblyAIProvider } from './assemblyAIProvider';
import { webSpeechProvider } from './webSpeechProvider';
import { elevenLabsProvider } from './elevenLabsProvider';

// Resolved once at module scope from a build-time flag — not a per-render choice,
// not a user preference. All provider modules ship in the bundle either way;
// unlike the AssemblyAI/ElevenLabs dev-direct key (sttTokenService.ts), there's
// no secret here that needs Rollup to eliminate the unused branches.
const PROVIDERS = {
  assemblyai: assemblyAIProvider,
  webspeech: webSpeechProvider,
  elevenlabs: elevenLabsProvider,
} as const;
const provider = PROVIDERS[FEATURES.SPEECH_PROVIDER];

function joinSpeech(existing: string, addition: string): string {
  if (!existing) return addition;
  return /\s$/.test(existing) ? existing + addition : `${existing} ${addition}`;
}

export interface UseSpeechInputResult {
  state: SpeechState;
  statusText?: string;
  error: SpeechError | null;
  isSupported: boolean;
  start: () => void;
  /** Resolves with the committed value once the session finishes finalizing. The
   *  caller decides what to do with it — leave it in the field (Stop) or send it
   *  immediately (Send); see MessageInput/VoiceRecordingBar. */
  stop: () => Promise<string>;
  /** Stops the session and discards everything captured this session, restoring
   *  the field to whatever it held right before `start()` was called. */
  cancel: () => void;
  dismissError: () => void;
}

/**
 * Provider-agnostic speech-input state. Talks only to the `SpeechProvider`
 * interface — swapping `FEATURES.SPEECH_PROVIDER` changes nothing here.
 *
 * The displayed `value` is always `committed + partial`: `committedRef` holds
 * every turn the provider has finalized so far (plus whatever was already in
 * the field before recording started), and `partialRef` holds the current
 * in-flight, still-revisable transcript for the turn in progress. Every
 * `onPartial` and `onFinal` recomputes and pushes that combined string via
 * `onChange` — the field fills in live as the provider streams text, instead
 * of only becoming visible on Stop/Send.
 *
 * Exactly one `useEffect`, with deps `[]`: its only job is the unmount cleanup and
 * the visibility/pagehide guard. All other imperative wiring (the provider's own
 * callbacks) is set up inside `start()`, in the same scope where the session
 * object is created — there is no ref read from an effect with stale deps
 * (CLAUDE.md silent-failure pattern 1).
 */
export function useSpeechInput(value: string, onChange: (value: string) => void): UseSpeechInputResult {
  const { getAccessToken } = useAuth();
  const [state, setState] = useState<SpeechState>('idle');
  const [statusText, setStatusText] = useState<string | undefined>(undefined);
  const [error, setError] = useState<SpeechError | null>(null);

  // Mirrors the live `value` prop (committed + partial), read (never a stale
  // closure) from inside stop(). Also written synchronously by applyDisplay
  // itself, since a React state update from `onChange` is not guaranteed to
  // have flushed into `value` by the time `stop()` resolves.
  const valueRef = useRef(value);
  valueRef.current = value;

  // Snapshot of `value` the instant recording starts, so `cancel()` can restore
  // it verbatim regardless of how much got committed in between.
  const preRecordingValueRef = useRef(value);

  // The finalized baseline partials build on top of — starts as whatever was
  // in the field before this session (same snapshot as preRecordingValueRef),
  // grows by one turn on every onFinal. Never includes the in-flight partial.
  const committedRef = useRef(value);
  // Current in-flight, still-revisable transcript for the turn in progress —
  // cleared on start() and on every onFinal (a final either supersedes it or,
  // for AssemblyAI's raw-then-formatted double emission, was never a partial
  // to begin with).
  const partialRef = useRef('');

  const sessionRef = useRef<SpeechSession | null>(null);

  const applyDisplay = useCallback(() => {
    const next = partialRef.current ? joinSpeech(committedRef.current, partialRef.current) : committedRef.current;
    valueRef.current = next;
    onChange(next);
  }, [onChange]);

  const handlePartial = useCallback(
    (text: string) => {
      partialRef.current = text;
      applyDisplay();
    },
    [applyDisplay],
  );

  const commitFinal = useCallback(
    (event: SpeechFinalEvent) => {
      const committed = committedRef.current;
      if (event.replacesPreviousText !== undefined) {
        if (!committed.endsWith(event.replacesPreviousText)) {
          // The user edited the field since the last insert — respect their
          // edit, don't re-insert the provider's revised version of it.
          partialRef.current = '';
          applyDisplay();
          return;
        }
        committedRef.current = committed.slice(0, committed.length - event.replacesPreviousText.length) + event.text;
      } else {
        committedRef.current = joinSpeech(committed, event.text);
      }
      partialRef.current = '';
      applyDisplay();
    },
    [applyDisplay],
  );

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        void sessionRef.current?.stop({ silent: true });
      }
    };
    const onPageHide = () => {
      void sessionRef.current?.stop({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      // Belt-and-suspenders: `speechSessionRegistry` is the defense that actually
      // survives the welcome<->chat remount (CLAUDE.md pattern 3), but stopping
      // via this component's own ref too means nothing lingers even in the
      // ordinary single-unmount case, with no wait for the registry's next start().
      void sessionRef.current?.stop({ silent: true });
    };
  }, []);

  const start = useCallback(() => {
    setError(null);
    preRecordingValueRef.current = valueRef.current;
    committedRef.current = valueRef.current;
    partialRef.current = '';
    const session = provider.createSession(
      {
        onStateChange: (nextState, text) => {
          setState(nextState);
          setStatusText(text);
        },
        onPartial: handlePartial,
        onFinal: commitFinal,
        onError: (err) => setError(err),
      },
      {
        getAccessToken: async () => {
          const token = await getAccessToken();
          if (!token) throw new Error('[STT] no access token available');
          return token;
        },
      },
    );
    sessionRef.current = session;
    void session.start();
  }, [commitFinal, handlePartial, getAccessToken]);

  const stop = useCallback(async (): Promise<string> => {
    const session = sessionRef.current;
    if (!session) return valueRef.current;
    await session.stop();
    return valueRef.current;
  }, []);

  const cancel = useCallback(() => {
    const session = sessionRef.current;
    const restore = preRecordingValueRef.current;
    void session?.stop({ silent: true }).then(() => {
      valueRef.current = restore;
      onChange(restore);
    });
  }, [onChange]);

  const dismissError = useCallback(() => {
    setError(null);
    setState('idle');
  }, []);

  return {
    state,
    statusText,
    error,
    isSupported: FEATURES.SPEECH_INPUT && provider.isSupported(),
    start,
    stop,
    cancel,
    dismissError,
  };
}
